import React, { useState, useEffect } from 'react';
import { styled } from '../../styles/stitches.config';
import { useUser } from '../../contextManager/user.context';
import useAuth, { ProfilePatch } from '../../hooks/auth.service';

// 1. Import the Card components and ActionButton
import { ProfilePhotoCard, CardHeader, CardContent, HeaderIconButton, CardFooter } from '../../styles/profile/layout.style';
import { ActionButton } from '../../styles/profile/profile.style';
import { PencilFill } from 'react-bootstrap-icons';

// --- Local Styles for this component ---
// For the display view
const InfoRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0.75rem 0',
  borderBottom: '1px solid $gray4',
  '&:last-child': { borderBottom: 'none' },
});
const InfoLabel = styled('span', { color: '$gray11', fontWeight: 500 });
const InfoValue = styled('span', { color: '$gray12', fontFamily: '$mono' });

// For the edit view form
const ManualField = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1.25rem',
});
const ManualLabel = styled('label', { fontWeight: 500, fontSize: '$sm' });
const ManualInput = styled('input', {
    border: '1px solid $elementBorder',
    borderRadius: 8,
    padding: '0.75rem',
    fontSize: '$sm',
    '&:focus': {
        borderColor: '$blue9',
        outline: 'none',
    }
});


// --- Helper function to mask the account number ---
const maskAccountNumber = (num?: string): string => {
  if (!num || num.length < 4) return '****';
  return `**** **** **** ${num.slice(-4)}`;
};


export const PayeeDetailsSection: React.FC = () => {
    const { userDetails } = useUser();
    const { patchProfile } = useAuth();

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    // State to hold form data during editing
    const [payeeData, setPayeeData] = useState({
        accountHolderName: userDetails?.accountHolderName || '',
        sortCode: userDetails?.sortCode || '',
        accountNumber: userDetails?.accountNumber || '',
    });

    // Handler for form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPayeeData(prev => ({ ...prev, [name]: value }));
    };

    // Reset form data if userDetails changes from the context
    useEffect(() => {
        setPayeeData({
            accountHolderName: userDetails?.accountHolderName || '',
            sortCode: userDetails?.sortCode || '',
            accountNumber: userDetails?.accountNumber || '',
        });
    }, [userDetails]);


    const handleSave = async () => {
      setIsSaving(true);
      try {
        // Construct the patch object with only the payee details
        const patch: ProfilePatch = { ...payeeData };
        await patchProfile(
          patch,
          { loading: 'Saving payee details...', success: 'Payee details saved.' },
        );
        setIsEditing(false); // Exit edit mode only on success
      } catch (error) {
        console.error("Failed to save payee details:", error);
        // Error toast is handled inside patchProfile hook
      } finally {
        setIsSaving(false);
      }
    };
  
    const handleCancel = () => {
      // Reset form fields to original values before canceling
      setPayeeData({
        accountHolderName: userDetails?.accountHolderName || '',
        sortCode: userDetails?.sortCode || '',
        accountNumber: userDetails?.accountNumber || '',
      });
      setIsEditing(false);
    };
  
    if (isEditing) {
      // --- RENDER THE EDITING VIEW ---
      return (
        <ProfilePhotoCard>
          <CardHeader>Edit Payee Details</CardHeader>
          <CardContent>
            <ManualField>
                <ManualLabel htmlFor="accountHolderName">Account Holder Name</ManualLabel>
                <ManualInput id="accountHolderName" name="accountHolderName" value={payeeData.accountHolderName} onChange={handleInputChange} />
            </ManualField>
            <ManualField>
                <ManualLabel htmlFor="sortCode">Sort Code (6 digits)</ManualLabel>
                <ManualInput id="sortCode" name="sortCode" value={payeeData.sortCode} onChange={handleInputChange} maxLength={6} placeholder="XX-XX-XX" />
            </ManualField>
            <ManualField>
                <ManualLabel htmlFor="accountNumber">Account Number (up to 16 digits)</ManualLabel>
                <ManualInput id="accountNumber" name="accountNumber" value={payeeData.accountNumber} onChange={handleInputChange} maxLength={16} />
            </ManualField>
          </CardContent>
          <CardFooter>
            <ActionButton intent="secondary" onClick={handleCancel} disabled={isSaving}>Cancel</ActionButton>
            <ActionButton intent="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </ActionButton>
          </CardFooter>
        </ProfilePhotoCard>
      );
    }
  
    // --- RENDER THE DEFAULT DISPLAY VIEW ---
    return (
      <ProfilePhotoCard>
        <CardHeader>
          <span>Payee Details</span>
          <HeaderIconButton onClick={() => setIsEditing(true)}>
            <PencilFill size={18} />
          </HeaderIconButton>
        </CardHeader>
        <CardContent>
            {payeeData.accountHolderName ? (
                <>
                    <InfoRow>
                        <InfoLabel>Account Holder</InfoLabel>
                        <InfoValue>{payeeData.accountHolderName}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>Sort Code</InfoLabel>
                        <InfoValue>{payeeData.sortCode}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>Account Number</InfoLabel>
                        <InfoValue>{maskAccountNumber(payeeData.accountNumber)}</InfoValue>
                    </InfoRow>
                </>
            ) : (
                <InfoValue css={{textAlign: 'center', padding: '2rem 0'}}>
                    No payee details on file. Click the pencil to add them.
                </InfoValue>
            )}
        </CardContent>
      </ProfilePhotoCard>
    );
  };